/**
 * Comitê de Investimentos (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_comite_investimentos.py).
 *
 * Região, período (padrão 20A, ou a data inicial no Personalizado) e aba ativa ficam na URL. A árvore de abas vem de /estrutura e
 * só o conteúdo da aba aberta é buscado (/conteudo), em vez de desenhar tudo de uma vez.
 */
import { Alert, Loader, Tabs } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { Elementos, type ElementoApi } from '../../components/Elementos';
import { BarraFiltros, CampoOpcoes, CampoPeriodo, GrupoFiltros } from '../../components/Filtros';
import { opcoesPeriodo, type OpcaoPeriodo } from '../../lib/periodos';
import { useFiltrosUrl } from '../../lib/url-state';
export interface NoAba {
  titulo: string;
  aba?: string;
  filhos?: NoAba[];
}
interface Estrutura {
  regiao: string;
  regioes: { valor: string; rotulo: string }[];
  abas: NoAba[];
}
const BASE = '/acompanhamentos-periodicos/comite-de-investimentos';
// Usados enquanto a primeira resposta não chega; a API devolve a lista definitiva.
const PERIODOS = opcoesPeriodo(['1a', '3a', '5a', '10a', '20a', 'max']);

interface Conteudo {
  aba: string;
  inicio: string;
  periodos: OpcaoPeriodo[];
  elementos: ElementoApi[];
}
export function primeiraFolha(no: NoAba): string | undefined {
  if (no.aba) return no.aba;
  for (const filho of no.filhos ?? []) {
    const folha = primeiraFolha(filho);
    if (folha) return folha;
  }
  return undefined;
}
export function contemFolha(no: NoAba, aba: string): boolean {
  return no.aba === aba || (no.filhos ?? []).some((f) => contemFolha(f, aba));
}
function mensagem(erro: unknown) {
  return erro instanceof ErroApi ? erro.message : 'Não foi possível carregar os dados.';
}
function NivelAbas({
  nos,
  abaAtiva,
  onAba,
  conteudo,
}: {
  nos: NoAba[];
  abaAtiva: string;
  onAba: (aba: string) => void;
  conteudo: React.ReactNode;
}) {
  const selecionado = nos.find((n) => contemFolha(n, abaAtiva)) ?? nos[0];
  if (!selecionado) return null;
  return (
    <Tabs
      value={selecionado.titulo}
      onChange={(titulo) => {
        const no = nos.find((n) => n.titulo === titulo);
        const folha = no && primeiraFolha(no);
        if (folha) onAba(folha);
      }}
      keepMounted={false}
    >
      <Tabs.List>
        {nos.map((no) => (
          <Tabs.Tab key={no.titulo} value={no.titulo}>
            {no.titulo}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      <Tabs.Panel value={selecionado.titulo} pt="md">
        {selecionado.filhos ? (
          <NivelAbas
            nos={selecionado.filhos}
            abaAtiva={abaAtiva}
            onAba={onAba}
            conteudo={conteudo}
          />
        ) : (
          conteudo
        )}
      </Tabs.Panel>
    </Tabs>
  );
}
export function ComiteInvestimentos() {
  const [filtros, definir] = useFiltrosUrl({ regiao: 'br', periodo: '20a', inicio: '', aba: '' });
  const estrutura = useQuery({
    queryKey: ['comite.estrutura', filtros.regiao],
    queryFn: ({ signal }) =>
      apiGet<Envelope<Estrutura>>(`${BASE}/estrutura`, { regiao: filtros.regiao }, signal),
  });
  const arvore = estrutura.data?.data.abas ?? [];
  // Aba da URL, se existir nesta região; senão, a primeira folha.
  const abaAtiva =
    (filtros.aba && arvore.some((n) => contemFolha(n, filtros.aba)) && filtros.aba) ||
    (arvore[0] && primeiraFolha(arvore[0])) ||
    '';
  const conteudo = useQuery({
    queryKey: ['comite.conteudo', filtros.regiao, filtros.periodo, filtros.inicio, abaAtiva],
    queryFn: ({ signal }) =>
      apiGet<Envelope<Conteudo>>(
        `${BASE}/conteudo`,
        {
          regiao: filtros.regiao,
          periodo: filtros.periodo,
          inicio: filtros.inicio,
          aba: abaAtiva,
        },
        signal,
      ),
    enabled: Boolean(abaAtiva),
    placeholderData: keepPreviousData,
  });
  const corpoAba = conteudo.isError ? (
    <Alert color="red">{mensagem(conteudo.error)}</Alert>
  ) : conteudo.data && conteudo.data.data.aba === abaAtiva ? (
    <Elementos elementos={conteudo.data.data.elementos} />
  ) : (
    <Loader />
  );
  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Comitê de Investimentos"
        descricao="Painel do Comitê de Investimentos: temas macroeconômicos e mercados, por região."
        dadosAte={conteudo.data?.meta.dados_ate}
        fonte="Banco Persevera (indicadores)"
      />
      <BarraFiltros carregando={conteudo.isFetching}>
        <GrupoFiltros titulo="Mercado">
          <CampoOpcoes
            rotulo="Região"
            valor={filtros.regiao}
            aoMudar={(regiao) => definir({ regiao })}
            opcoes={
              estrutura.data?.data.regioes ?? [
                { valor: 'br', rotulo: 'Brasil' },
                { valor: 'us', rotulo: 'Estados Unidos' },
              ]
            }
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Período">
          <CampoPeriodo
            periodo={filtros.periodo}
            inicio={filtros.inicio}
            inicioEfetivo={conteudo.data?.data.inicio}
            opcoes={conteudo.data?.data.periodos ?? PERIODOS}
            min="2000-01-01"
            aoMudar={definir}
          />
        </GrupoFiltros>
      </BarraFiltros>
      {estrutura.isError && <Alert color="red">{mensagem(estrutura.error)}</Alert>}
      {estrutura.isPending && <Loader />}
      <AvisosApi avisos={[estrutura.data?.meta.avisos, conteudo.data?.meta.avisos]} />
      <Secao
        titulo="Painel"
        legenda="Escolha o tema ou o mercado nas abas; cada aba é carregada ao ser aberta."
      >
        {arvore.length > 0 && (
          <NivelAbas
            nos={arvore}
            abaAtiva={abaAtiva}
            onAba={(aba) => definir({ aba })}
            conteudo={corpoAba}
          />
        )}
      </Secao>
    </Pagina>
  );
}
